import MuiTabs from '@mui/material/Tabs';
import { Stack } from '@src/common/components/Stack';
import type { CSSInterpolation } from '@src/common/styles/Styles';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { CheckedFields } from '@src/orders/domain/CheckedFields';
import { hasCategory } from '@src/orders/domain/CheckedFields';
import type { PermitInstruction } from '@src/orders/domain/PermitInstruction';
import { EditOrderTab } from '@src/orders/EditOrderTab';
import { usePermitFormErrors } from '@src/orders/hooks/usePermitFormErrors';
import { matchesBetween } from '@src/orders/MatchesBetween';
import { fields } from '@src/orders/schemas/EditOrderFormInputs';
import {
  AxleFormName,
  AxleOverallWeightName,
} from '@src/permitField/AxleInformationFields';
import type { CategoryKey } from '@src/permitField/Fields';
import { overallDimensionsFields } from '@src/permitField/OverallDimensionsFields';
import { PONumberField } from '@src/permitField/PONumberField';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import { globalTripsFields } from '@src/permitField/TripInformationFields';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  canAddPermits: boolean;
  currentTab: TabKey;
  permitInstructions: PermitInstruction[];
  onChangeSection: (newTabKey: TabKey) => void;
  checkedFields: CheckedFields;
}

export type TabKey = CategoryKey | 'instructions' | 'permits';

interface Tab {
  key: TabKey;
  label: string;
  inErrorFields: string[];
}

const tabs: ({ canAddPermits }: { canAddPermits: boolean }) => Tab[] = ({
  canAddPermits,
}: {
  canAddPermits: boolean;
}) => [
  ...(canAddPermits
    ? [
        {
          key: 'permits' as TabKey,
          label: 'orderSections.permits',
          inErrorFields: [],
        },
      ]
    : []),
  {
    key: 'instructions',
    label: 'orderSections.instructions',
    inErrorFields: [],
  },
  {
    key: 'carrierAndPermitDelivery',
    label: 'orderSections.carrierAndPermitDelivery',
    inErrorFields: [
      ...Object.keys(fields.carrierAndPermitDelivery.companyInformation),
      ...Object.keys(fields.carrierAndPermitDelivery.companyAddress),
      ...Object.keys(fields.carrierAndPermitDelivery.driverInformation),
      ...Object.keys(fields.carrierAndPermitDelivery.orderInformation),
      ...Object.keys(
        fields.carrierAndPermitDelivery.permitDeliveryAndNotifications,
      ),
      PONumberField.fieldName,
    ],
  },
  {
    key: 'accountsAndInsurances',
    label: 'orderSections.accountsAndInsurances',
    inErrorFields: [
      ...Object.keys(fields.accountsAndInsurances.account),
      ...Object.keys(fields.accountsAndInsurances.insuranceInformation),
      ...Object.keys(fields.accountsAndInsurances.juridictionIds),
    ],
  },
  {
    key: 'haulingEquipment',
    label: 'orderSections.haulingEquipment',
    inErrorFields: [
      ...Object.keys(fields.haulingEquipment.tractorInformation),
      ...Object.keys(fields.haulingEquipment.tractorDimensions),
      ...Object.keys(fields.haulingEquipment.tractorLicense),
      ...Object.keys(fields.haulingEquipment.tractorOwner),
      ...Object.keys(fields.haulingEquipment.trailers).map((f) => `${f}.*`),
    ],
  },
  {
    key: 'loadInformation',
    label: 'orderSections.loadInformation',
    inErrorFields: [
      ...Object.keys(fields.loadInformation.loadInformation),
      ...Object.keys(fields.loadInformation.mobileHome),
    ],
  },
  {
    key: 'axlesConfiguration',
    label: 'orderSections.axlesConfiguration',
    inErrorFields: [
      `${AxleFormName}.*`,
      TractorAxlesFormUtils.fieldName,
      ...Object.keys(overallDimensionsFields),
      AxleOverallWeightName,
    ],
  },
  {
    key: 'routingAndStates',
    label: 'orderSections.routingAndStates',
    inErrorFields: [...Object.keys(globalTripsFields), 'trips.*'],
  },
  {
    key: 'notesAndDocuments',
    label: 'orderSections.notesAndDocuments',
    inErrorFields: Object.keys(fields.notesAndDocuments.comments),
  },
];

export const EditOrderTabs = ({
  canAddPermits,
  currentTab,
  onChangeSection,
  checkedFields,
  permitInstructions,
}: Props) => {
  const { t } = useTranslation();
  const changeSection = (_: SyntheticEvent, newTabKey: TabKey) => {
    onChangeSection(newTabKey);
  };
  const { formErrorKeys } = usePermitFormErrors();

  const tabProps = (tabKey: TabKey) => {
    return {
      id: `vertical-tab-${tabKey}`,
      'aria-controls': `vertical-tabpanel-${tabKey}`,
      className: canAddPermits
        ? tabClassWithAddPermits
        : tabClassWithoutAddPermits,
      value: tabKey,
    };
  };

  return (
    <Stack direction="column">
      <MuiTabs
        orientation="vertical"
        value={currentTab}
        TabIndicatorProps={{
          style: { display: 'none' },
        }}
        onChange={changeSection}
        aria-label="edit-order-pick-section"
        style={{
          width: '100%',
          gap: '200px',
        }}
      >
        {tabs({ canAddPermits })
          .filter(
            ({ key }) =>
              key === 'permits' ||
              hasCategory(key, checkedFields, permitInstructions),
          )
          .map((tab) => (
            <EditOrderTab
              key={tab.key}
              label={t(tab.label)}
              inError={matchesBetween(formErrorKeys, tab.inErrorFields)}
              {...tabProps(tab.key)}
            />
          ))}
      </MuiTabs>
    </Stack>
  );
};

const addPermitLinkStyle = {
  '&:first-of-type': {
    borderBottom: `1px solid ${theme.palette.border.primary}`,
  },
};

const tabLinksStyle: CSSInterpolation = {
  '&.Mui-selected': {
    color: theme.palette.primary.primary,
    borderRadius: '100px',
  },
  height: '60px',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'left',
};

const tabClassWithAddPermits = css({
  ...tabLinksStyle,
  ...addPermitLinkStyle,
});

const tabClassWithoutAddPermits = css({
  ...tabLinksStyle,
});
